// vi: filetype=javascript.jsx
import { CloseButton, isMobile, UnstyledButton } from "@classdojo/web";
import useOnFirstRender from "@classdojo/web/hooks/useOnFirstRender";
import { LabelText } from "@classdojo/web/nessie";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { TranslatedString } from "@classdojo/web/pods/i18n/translate";
import { translate } from "@classdojo/web/utils/translate";
import FocusTrap from "focus-trap-react";
import { useNavigate } from "react-router-dom";
import WelcomeCallout from "./WelcomeCallout";
import ExperiencesContainer from "src/pods/ideas/components/experiences/ExperiencesContainer";
import logEvent from "src/utils/logEvent";
import { metrics } from "src/utils/metrics";

type LaunchpadProps = {
  apps: {
    href: string;
    title: TranslatedString;
    backgroundColor: string;
    icon: string;
  }[];
  classId: string;
  mountPoint: string;
  remoteClose: () => void;
  dismissWelcomeNux?: () => void;
  shouldRenderWelcome?: boolean;
};

export default function Launchpad({
  shouldRenderWelcome,
  dismissWelcomeNux,
  apps,
  classId,
  mountPoint,
  remoteClose,
}: LaunchpadProps) {
  useOnFirstRender(() => {
    metrics.increment("main.open");
    logEvent({ eventName: "main.open" });
  });

  const navigate = useNavigate();

  return (
    <FocusTrap active={!isMobile.any}>
      <div sx={pageStyle} id="toolkitLaunchpad">
        {mountPoint === "teach" && (
          <CloseButton
            data-name="launchpad:close_button"
            aria-label={translate({
              str: "dojo.toolkit:other_actions.close_toolkit",
              fallback: "Close toolkit",
            })}
            close={remoteClose}
          />
        )}
        {mountPoint === "mobile" && shouldRenderWelcome && (
          <section className="callout" sx={{ width: "100%", marginBottom: "dt_l" }}>
            <WelcomeCallout dismissWelcomeNux={dismissWelcomeNux} />
          </section>
        )}
        <section
          className="launchpad"
          sx={{
            width: "100%",
            display: "grid",
            gap: "dt_xxl",
            marginY: "dt_xxl",
            "@media screen and (min-width: 100px)": { gridTemplateColumns: "repeat(1, 1fr)" },
            "@media screen and (min-width: 350px)": { gridTemplateColumns: "repeat(2, 1fr)" },
            "@media screen and (min-width: 480px)": { gridTemplateColumns: "repeat(3, 1fr)" },
            "@media screen and (min-width: 650px)": { gridTemplateColumns: "repeat(auto-fill, 1fr)" },
            "@media screen and (min-width: 1000px)": { gridTemplateColumns: "repeat(5, 1fr)" },
          }}
        >
          {apps.map((appButton) => {
            const { title, icon, backgroundColor, href } = appButton;

            return (
              <UnstyledButton
                data-name="launchpad:launchpad_app_icon"
                key={title}
                onClick={() => navigate(href)}
                className="launchpad__app"
                sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
              >
                <div
                  sx={{
                    backgroundColor: RAW_cssValue(backgroundColor || "#c4c4c4"),
                    width: "70px",
                    height: "70px",
                    borderRadius: "dt_radius_s",
                    boxShadow: "dt_shadow_shadezies",
                    userSelect: "none",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img src={icon} alt="" />
                </div>
                <LabelText
                  level={2}
                  sx={{
                    marginTop: "dt_s",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textAlign: "center",
                  }}
                >
                  {title}
                </LabelText>
              </UnstyledButton>
            );
          })}
        </section>
        {mountPoint === "mobile" && <ExperiencesContainer classId={classId} mountPoint={mountPoint} />}
      </div>
    </FocusTrap>
  );
}

const pageStyle: ThemeUIStyleObject = {
  width: "100vw",
  minHeight: "100vh",
  backgroundColor: "dt_background_primary",
  overflowX: "hidden",
  padding: "dt_xxl",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};
